import { isEqual } from "lodash";
import qs from "qs";

import { DocumentsSearchRequest } from "lib/generated/app-service-gql/graphql";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { CurrentUserData } from "lib/hooks/api/users/useCurrentUser";
import {
  DocumentCategory,
  DocumentRelationshipType,
  DocumentsSort,
  DocumentsSortOrder,
} from "lib/types/graphQLEnums";
import { BuyerCategoryAssignment, ColumnSetting, CountryRegion } from "lib/types/models";
import { getMinRelevanceScore } from "lib/utils/signalUtils";

const HITECH_GUID = "659dc990-a36b-44df-a08e-b6c0730f5019";

export type DocumentColumns =
  | "title"
  | "buyers"
  | "signalScore"
  | "category"
  | "publishedAt"
  | "actions";

export const DEFAULT_DOCUMENT_COLUMNS: ColumnSetting<DocumentColumns>[] = [
  { title: "Document title", field: "title", disabled: true },
  { title: "Actions", field: "actions" },
  { title: "Buyer(s)", field: "buyers" },
  { title: "Type", field: "category" },
  { title: "Publish date", field: "publishedAt" },
];

export function createDocumentColumns({ excludedColumns }: { excludedColumns: DocumentColumns[] }) {
  return DEFAULT_DOCUMENT_COLUMNS.filter((col) => !excludedColumns.includes(col.field));
}

export const DEFAULT_PAGINATION = { current: 1, pageSize: 10 };

export type SortField = "title" | "category" | "publishedAt" | "signalScore" | "createdAt";

const validSortFields: SortField[] = [
  "signalScore",
  "title",
  "category",
  "publishedAt",
  "createdAt",
];

type DateFilter = { from?: string; relativeFrom?: string; to?: string; relativeTo?: string };

type Pagination = { pageSize: number; current: number };

export type DocumentFilters = {
  signalScore: RelevanceScoreEnum[];
  title: string;
  organisationIds?: string[];
  buyerTypes?: string[];
  buyerListIds?: string[];
  buyerCountryRegions?: CountryRegion;
  keywords?: string[];
  categories?: DocumentCategory[];
  publishDate?: { filter?: DateFilter };
  sort: {
    field: SortField;
    order: "ASC" | "DESC";
  };
};

export const DEFAULT_DOCUMENT_FILTERS: DocumentFilters = {
  title: "",
  keywords: [],
  signalScore: [],
  publishDate: {},
  categories: [],
  sort: {
    field: "publishedAt",
    order: "DESC",
  },
};

export const filterKeyToLabel: Record<keyof DocumentFilters, string> = {
  signalScore: "Signal score",
  keywords: "Keywords",
  categories: "Document type",
  publishDate: "Published date",
  organisationIds: "Buyers",
  buyerTypes: "Buyer type",
  buyerListIds: "Buyer lists",
  buyerCountryRegions: "Buyer location",
  title: "",
  sort: "",
};

export function parseDocumentsUrlState(
  state: unknown,
  defaultFilters = DEFAULT_DOCUMENT_FILTERS,
): DocumentFilters {
  if (typeof state !== "object" || state === null) {
    return defaultFilters;
  }

  const filters = state as DocumentFilters;

  return {
    ...filters,
    signalScore: filters.signalScore ?? [],
    keywords: filters.keywords ?? [],
  };
}

export function isValidSortField(field?: string): SortField | undefined {
  if (field && validSortFields.includes(field as SortField)) {
    return field as SortField;
  }
  return undefined;
}

export const documentTypes = [
  { label: "Report and accounts", value: DocumentCategory.ReportAndAccounts },
  { label: "Strategy", value: DocumentCategory.Strategy },
  { label: "Budget", value: DocumentCategory.Budget },
  { label: "FOI", value: DocumentCategory.Foi },
  {
    label: "Specific report e.g. public health, crime, etc.",
    value: DocumentCategory.SpecificReport,
  },
  {
    label: "Specific strategy e.g. housing, transport, etc.",
    value: DocumentCategory.SpecificStrategy,
  },
  { label: "Meeting minutes", value: DocumentCategory.MeetingMinutes },
  { label: "News and communication", value: DocumentCategory.NewsAndCommunication },
  { label: "Case study", value: DocumentCategory.CaseStudy },
  { label: "Guidance and regulation", value: DocumentCategory.GuidanceAndRegulation },
  { label: "Consultation and research", value: DocumentCategory.ConsultationAndResearch },
  { label: "Statistics", value: DocumentCategory.Statistics },
  { label: "Tribunal decisions", value: DocumentCategory.TribunalDecisions },
  { label: "Internal operations", value: DocumentCategory.InternalOperations },
  { label: "Other", value: DocumentCategory.Other },
];

export const simplifiedDocumentTypes = [
  { label: "Report and accounts", value: DocumentCategory.ReportAndAccounts },
  { label: "Strategy", value: DocumentCategory.Strategy },
  { label: "Budget", value: DocumentCategory.Budget },
  { label: "FOI", value: DocumentCategory.Foi },
  {
    label: "Specific report e.g. public health, crime, etc.",
    value: DocumentCategory.SpecificReport,
  },
  { label: "Meeting minutes", value: DocumentCategory.MeetingMinutes },
  { label: "News and communication", value: DocumentCategory.NewsAndCommunication },
  { label: "Case study", value: DocumentCategory.CaseStudy },
  { label: "Other", value: DocumentCategory.Other },
];

// FE categories are less granular than on BE to reduce complexity for users
export const simplifiedCategoryMap: Pick<
  Record<DocumentCategory, DocumentCategory[]>,
  DocumentCategory.Other | DocumentCategory.Strategy
> = {
  [DocumentCategory.Strategy]: [DocumentCategory.SpecificStrategy, DocumentCategory.Strategy],
  [DocumentCategory.Other]: [
    DocumentCategory.Other,
    DocumentCategory.GuidanceAndRegulation,
    DocumentCategory.ConsultationAndResearch,
    DocumentCategory.Statistics,
    DocumentCategory.TribunalDecisions,
    DocumentCategory.InternalOperations,
  ],
};

// frontend / merged categories
export const mergedDocumentTypeMap = documentTypes.reduce<{ [key: string]: string }>(
  (map, item) => {
    if (item.value in simplifiedCategoryMap) {
      simplifiedCategoryMap[item.value as keyof typeof simplifiedCategoryMap].forEach(
        (backendCategory) => {
          map[backendCategory] = item.label;
        },
      );
    } else if (
      !Object.values(simplifiedCategoryMap).some((categories) => categories.includes(item.value))
    ) {
      map[item.value] = item.label;
    }
    return map;
  },
  {},
);

// backend / all categories
export const fullDocumentTypeMap = documentTypes.reduce<{ [key: string]: string }>((map, item) => {
  map[item.value] = item.label;
  return map;
}, {});

export function isAllDocumentTypesUser(user: CurrentUserData | undefined) {
  if (!user) {
    return false;
  }

  return user.company.guid === HITECH_GUID || user.email === "francesco@stotles.com";
}

export function getDocumentTypeLabel(
  type: DocumentCategory | string,
  user: CurrentUserData | undefined,
) {
  if (isAllDocumentTypesUser(user)) {
    return fullDocumentTypeMap[type] ?? "--";
  }
  return mergedDocumentTypeMap[type] ?? "--";
}

function mapToSortOrder(sort: { field: SortField; order: "ASC" | "DESC" }) {
  const order = sort.order === "ASC" ? DocumentsSortOrder.Asc : DocumentsSortOrder.Desc;
  const getField = (sortField: SortField) => {
    switch (sortField) {
      case "title":
        return DocumentsSort.Title;
      case "publishedAt":
        return DocumentsSort.PublishDate;
      case "signalScore":
        return DocumentsSort.Score;
      case "category":
        return DocumentsSort.Category;
      case "createdAt":
        return DocumentsSort.CreateDate;
      default:
        return DocumentsSort.Score;
    }
  };

  return {
    field: getField(sort.field),
    order: order,
  };
}

/*
The 'collection in progress' banner will be temporarily
displayed on the buyer profiles matching these categories
*/
export const INCOMPLETE_COVERAGE_BUYERS = ["Local", "Healthcare", "Education", "Blue light"];

/** Whether any of the buyer's categories sits under one of the INCOMPLETE_COVERAGE_BUYERS parents. */
export function isIncompleteCoverageBuyer(
  categories: Pick<BuyerCategoryAssignment, "parent_category_name">[],
): boolean {
  return categories.some(
    ({ parent_category_name }) =>
      !!parent_category_name && INCOMPLETE_COVERAGE_BUYERS.includes(parent_category_name),
  );
}

// Mime types that Apryse WebViewer can directly render in the browser — i.e. the
// original file can be fed straight to the viewer without any server-side
// PDF conversion.
// We still prefer to feed it the converted PDF variant when available, since that
// has better support + is required for many features to work.
// Ensure any new extension is added to either APRYSE_PDF_CONVERSION_EXTENSIONS or LIBRE_OFFICE_EXTENSIONS
// on the server-side code so there should be a PDF variant available for it in the viewer.
// https://docs.apryse.com/web/guides/file-format-support - Client only column
const APRYSE_NATIVE_MIME_TYPE_TO_EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  // Word (.doc, .docx, .dot, .dotx & .dotm are supported)
  "application/msword": "doc", // also covers .dot
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": "dotx",
  "application/vnd.ms-word.document.macroenabled.12": "docm",
  "application/vnd.ms-word.template.macroenabled.12": "dotm",
  // Excel (.xls, .xlsx, .xlsb & .xlt are supported)
  "application/vnd.ms-excel": "xls", // also covers .xlt
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel.sheet.macroenabled.12": "xlsm",
  // PowerPoint (.ppt, .pptx, .pptm, .pot & .pps are supported)
  "application/vnd.ms-powerpoint": "ppt", // also covers .pot, .pps
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.ms-powerpoint.presentation.macroenabled.12": "pptm",
  // Images
  "image/jpeg": "jpg",
  "image/png": "png",
};

export function getApryseNativeExtension(mimeType: string | null | undefined): string | undefined {
  if (!mimeType) return undefined;
  return APRYSE_NATIVE_MIME_TYPE_TO_EXTENSION[mimeType];
}

export type DocumentViewerSource = {
  url: string;
  // Extension hint for Apryse WebViewer. Undefined means PDF (the default).
  extension: string | undefined;
  // True when we're rendering the original Office file directly because no
  // server-converted PDF is available. Lower fidelity — tables and advanced
  // Office features may not render correctly.
  isInFallbackMode: boolean;
};

// Resolve which URL+extension to feed to Apryse WebViewer.
// Prefers the server-converted PDF.
// Falls back to rendering the original file natively when
// no PDF variant is available and Apryse can render the mime type directly.
// Returns null when the document cannot be previewed at all.
export function getDocumentViewerSource(
  pdfDocumentUrl: string | null | undefined,
  downloadUrl: string | null | undefined,
  originalMimeType: string | null | undefined,
): DocumentViewerSource | null {
  if (pdfDocumentUrl) {
    return { url: pdfDocumentUrl, extension: undefined, isInFallbackMode: false };
  }
  const extension = getApryseNativeExtension(originalMimeType);
  if (extension && downloadUrl) {
    // For native PDFs, pdfDocumentUrl and downloadUrl resolve to the same ORIGINAL
    // variant upstream — a divergence here is a transient call failure, not an
    // incomplete-processing state, so don't flag fallback mode.
    return { url: downloadUrl, extension, isInFallbackMode: extension !== "pdf" };
  }
  return null;
}

export function expandDocumentCategories(
  categories: DocumentCategory[] | undefined,
  user: CurrentUserData | undefined,
): DocumentCategory[] | undefined {
  if (!categories) {
    return undefined;
  }
  if (isAllDocumentTypesUser(user)) {
    return categories;
  }

  const expandedCategories = categories.flatMap((category) => {
    if (category in simplifiedCategoryMap) {
      return simplifiedCategoryMap[category as keyof typeof simplifiedCategoryMap];
    }
    return category;
  });
  return expandedCategories;
}

export function collapseDocumentCategories(
  categories: DocumentCategory[] | undefined,
  user: CurrentUserData | undefined,
): DocumentCategory[] | undefined {
  if (!categories) {
    return undefined;
  }

  if (isAllDocumentTypesUser(user)) {
    return categories;
  }

  const collapsedSet = new Set<DocumentCategory>();

  categories.forEach((category) => {
    const parent = getParentCategory(category) ?? category;
    collapsedSet.add(parent);
  });

  return Array.from(collapsedSet);
}

function getParentCategory(value: DocumentCategory): DocumentCategory | undefined {
  for (const parent of Object.keys(
    simplifiedCategoryMap,
  ) as (keyof typeof simplifiedCategoryMap)[]) {
    const childCategories = simplifiedCategoryMap[parent];

    if (childCategories.includes(value)) {
      return parent;
    }
  }

  return undefined;
}

export function convertDocumentFiltersToSearchRequest(
  filters: DocumentFilters,
  user: CurrentUserData | undefined,
  pagination?: Pagination,
): DocumentsSearchRequest {
  const sortFields = mapToSortOrder(filters.sort);
  const categories = expandDocumentCategories(filters.categories, user);

  return {
    buyerFilters: {
      buyers: filters.organisationIds,
      buyerCategories: filters.buyerTypes,
      buyerLists: filters.buyerListIds,
      buyerCountryCodes: filters.buyerCountryRegions?.countries,
      buyerRegions: filters.buyerCountryRegions?.regions,
    },
    minScore: getMinRelevanceScore(filters.signalScore),
    publishDate: filters.publishDate ?? {},
    categories,
    keywords: filters.keywords ?? [],
    title: filters.title,
    relationshipType: DocumentRelationshipType.Organisation,
    limit: pagination?.pageSize ?? 10,
    page: pagination?.current ?? 1,
    sort: sortFields.field,
    sortOrder: sortFields.order,
  };
}

export type FilterSection = "buyers";

export function documentsUrl(filters: DocumentFilters): string {
  const params = qs.stringify(
    { documentFilters: filters },
    { arrayFormat: "brackets", addQueryPrefix: true },
  );
  return `/documents${params}`;
}

// Covers the sort fields as well as the columns, so the "sorted by" label still reads for a sort
// that arrives in the URL on a field no column shows.
const columnCopies: Record<DocumentColumns | SortField, string> = {
  ...DEFAULT_DOCUMENT_COLUMNS.reduce(
    (acc, { title, field }) => ({ ...acc, [field]: title }),
    {} as Record<DocumentColumns, string>,
  ),
  signalScore: "Signal score",
  createdAt: "Upload date",
};

export const getDocumentColumnCopy = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as keyof typeof columnCopies];
};

export function convertDocumentFiltersToSavedViewFilters(filters: DocumentFilters) {
  // Create a copy of filters without the sort field
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { sort, ...filtersWithoutSort } = filters;
  return {
    ...filtersWithoutSort,
    publishDate: filters.publishDate?.filter ? { filter: filters.publishDate.filter } : undefined,
  };
}

const normaliseDateFilter = (dateFilter?: DateFilter) => {
  if (!dateFilter) return undefined;

  const isDateFilterActive =
    dateFilter.from || dateFilter.relativeFrom || dateFilter.to || dateFilter.relativeTo;

  if (!isDateFilterActive) return undefined;

  // Prefer relative dates over absolute since setting a relative also sets an absolute date as a fallback
  let normalisedDateFilter: DateFilter = {};
  if (dateFilter.relativeFrom) {
    normalisedDateFilter.relativeFrom = dateFilter.relativeFrom;
  } else if (dateFilter.from) {
    normalisedDateFilter.from = dateFilter.from;
  }

  if (dateFilter.relativeTo) {
    normalisedDateFilter.relativeTo = dateFilter.relativeTo;
  } else if (dateFilter.to) {
    normalisedDateFilter.to = dateFilter.to;
  }

  return { filter: normalisedDateFilter };
};

function normaliseFilters(filters: DocumentFilters): DocumentFilters {
  const normalisedPublishDate = normaliseDateFilter(filters.publishDate?.filter);
  return {
    title: filters.title,
    keywords: [...(filters.keywords ?? [])].sort(),
    signalScore: [...(filters.signalScore ?? [])].sort(),
    categories: [...(filters.categories ?? [])].sort(),
    sort: {
      field: filters.sort?.field ?? "publishedAt",
      order: filters.sort?.order ?? "DESC",
    },
    organisationIds: [...(filters.organisationIds ?? [])].sort(),
    buyerTypes: [...(filters.buyerTypes ?? [])].sort(),
    buyerListIds: [...(filters.buyerListIds ?? [])].sort(),
    buyerCountryRegions: {
      countries: [...(filters.buyerCountryRegions?.countries ?? [])].sort(),
      regions: [...(filters.buyerCountryRegions?.regions ?? [])].sort(),
      selected: [...(filters.buyerCountryRegions?.selected ?? [])].sort(),
    },
    ...(normalisedPublishDate ? { publishDate: normalisedPublishDate } : {}),
  };
}

export function areFiltersEqual(filtersA: DocumentFilters, filtersB: DocumentFilters): boolean {
  const normalizedA = normaliseFilters(filtersA);
  const normalizedB = normaliseFilters(filtersB);

  return isEqual(normalizedA, normalizedB);
}
